import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currencyCtx";
import { fmt } from "@/lib/currency";
import { Plus, Edit2, Trash2, Crown, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const POSITIONS = [
  "Founding Equity Partner",
  "Global Equity Partner",
  "Equity Partner",
  "Associate Partner",
];

const EMPTY = {
  name: "",
  email: "",
  position: "Equity Partner",
  leadership_role: "",
  is_founder: false,
  tenure_years: 0,
  region: "",
  capital_contribution: 0,
  notes: "",
};

export default function AdminPartners() {
  const { currency, rate } = useCurrency();
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [p, c, u] = await Promise.all([
      api.get("/partners"),
      api.get("/config"),
      api.get("/users"),
    ]);
    setPartners(p.data);
    setConfig(c.data);
    setUsers(u.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const roleFor = (partnerId) => users.find((u) => u.partner_id === partnerId)?.role || null;

  const setRole = async (partner, newRole) => {
    try {
      await api.put(`/partners/${partner.partner_id}/role`, { role: newRole });
      toast.success(`${partner.name} is now ${newRole === "admin" ? "an administrator" : "a partner"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShow(true); };
  const openEdit = (p) => {
    setEditing(p.partner_id);
    setForm({ ...EMPTY, ...p, leadership_role: p.leadership_role || "" });
    setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tenure_years: Number(form.tenure_years) || 0,
        capital_contribution: Number(form.capital_contribution) || 0,
        leadership_role: form.leadership_role || null,
      };
      if (editing) {
        await api.put(`/partners/${editing}`, payload);
        toast.success("Partner updated");
      } else {
        await api.post("/partners", payload);
        toast.success("Partner added");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner? This cannot be undone.")) return;
    await api.delete(`/partners/${id}`);
    toast.success("Partner removed");
    load();
  };

  const leadershipKeys = config ? Object.keys(config.position_points) : [];

  return (
    <div className="anim-fade-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow mb-2">Roster</div>
          <h1 className="display-serif text-5xl text-atf-brand">Partners</h1>
          <p className="text-atf-text2 mt-3 max-w-xl">The people who own the firm.</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-partner-button"
          className="bg-atf-brand text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-brand2 flex items-center gap-2"
        >
          <Plus size={14} /> Add Partner
        </button>
      </div>

      <div className="border border-atf-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="partners-table">
          <thead>
            <tr className="border-b border-atf-border">
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Class</Th>
              <Th>Leadership</Th>
              <Th align="right">Tenure (yrs)</Th>
              <Th align="right">Capital</Th>
              <Th>Access</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-atf-text2">Loading…</td></tr>
            )}
            {!loading && partners.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-atf-text2">
                <div className="font-serif text-xl text-atf-brand mb-2">No partners yet.</div>
                Add your first founder to begin.
              </td></tr>
            )}
            {partners.map((p) => {
              const role = roleFor(p.partner_id);
              return (
              <tr key={p.partner_id} className="border-b border-atf-border last:border-b-0 hover:bg-atf-subtle/50" data-testid={`partner-row-${p.partner_id}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.name}</span>
                    {p.is_founder && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-atf-gold/10 border border-atf-gold/30 text-[10px] uppercase tracking-wider text-atf-gold font-semibold">
                        <Crown size={10} /> Founder
                      </span>
                    )}
                  </div>
                </Td>
                <Td><span className="font-mono text-xs text-atf-text2">{p.email}</span></Td>
                <Td><span className="text-xs">{p.position}</span></Td>
                <Td><span className="text-xs text-atf-text2">{p.leadership_role || "—"}</span></Td>
                <Td align="right" mono>{p.tenure_years}</Td>
                <Td align="right" mono>{fmt(p.capital_contribution, currency, rate)}</Td>
                <Td>
                  {role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-atf-brand text-white text-[10px] uppercase tracking-wider font-semibold" data-testid={`role-badge-${p.partner_id}`}>
                      <ShieldCheck size={10} /> Admin
                    </span>
                  ) : role === "partner" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-atf-subtle border border-atf-border text-atf-text2 text-[10px] uppercase tracking-wider font-semibold" data-testid={`role-badge-${p.partner_id}`}>
                      <UserIcon size={10} /> Partner
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-atf-text2 font-semibold" data-testid={`role-badge-${p.partner_id}`} title="No user record — will be created on first sign-in">Pending</span>
                  )}
                </Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-3">
                    {role === "admin" ? (
                      <button onClick={() => setRole(p, "partner")} data-testid={`demote-${p.partner_id}`} title="Revert to partner" className="text-[10px] uppercase tracking-wider font-semibold text-atf-text2 hover:text-atf-brand border-b border-transparent hover:border-atf-brand pb-0.5">
                        Make partner
                      </button>
                    ) : (
                      <button onClick={() => setRole(p, "admin")} data-testid={`promote-${p.partner_id}`} title="Promote to admin" className="text-[10px] uppercase tracking-wider font-semibold text-atf-text2 hover:text-atf-brand border-b border-transparent hover:border-atf-brand pb-0.5">
                        Make admin
                      </button>
                    )}
                    <button onClick={() => openEdit(p)} data-testid={`edit-${p.partner_id}`} className="text-atf-text2 hover:text-atf-brand">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => remove(p.partner_id)} data-testid={`delete-${p.partner_id}`} className="text-atf-text2 hover:text-atf-negative">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </Td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <div className="bg-white border border-atf-border max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="partner-modal">
            <div className="px-8 py-6 border-b border-atf-border">
              <div className="eyebrow mb-1">{editing ? "Edit Partner" : "Add Partner"}</div>
              <h2 className="font-serif text-2xl text-atf-brand">{editing ? form.name : "New partner"}</h2>
            </div>
            <form onSubmit={submit} className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT} />
              </Field>
              <Field label="Email">
                <input data-testid="form-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT} />
              </Field>
              <Field label="Partnership Class">
                <select data-testid="form-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className={INPUT}>
                  {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Leadership Role">
                <select data-testid="form-leadership" value={form.leadership_role || ""} onChange={(e) => setForm({ ...form, leadership_role: e.target.value })} className={INPUT}>
                  <option value="">— None —</option>
                  {leadershipKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <Field label="Tenure (years)">
                <input data-testid="form-tenure" type="number" step="0.5" min={0} value={form.tenure_years} onChange={(e) => setForm({ ...form, tenure_years: e.target.value })} className={INPUT} />
              </Field>
              <Field label="Capital Contribution (₹)">
                <input data-testid="form-capital" type="number" min={0} value={form.capital_contribution} onChange={(e) => setForm({ ...form, capital_contribution: e.target.value })} className={INPUT} />
              </Field>
              <Field label="Region">
                <input data-testid="form-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={INPUT} placeholder="Americas / EMEA / APAC" />
              </Field>
              <Field label="Founder Status">
                <label className="flex items-center gap-2 py-2.5">
                  <input data-testid="form-founder" type="checkbox" checked={!!form.is_founder} onChange={(e) => setForm({ ...form, is_founder: e.target.checked })} className="w-4 h-4 accent-atf-brand" />
                  <span className="text-sm text-atf-text2">Founding Equity Partner</span>
                </label>
              </Field>
              <Field label="Notes" full>
                <textarea data-testid="form-notes" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={INPUT} />
              </Field>
              <div className="col-span-full flex items-center justify-end gap-3 pt-4 border-t border-atf-border">
                <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 border border-atf-border text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-subtle" data-testid="cancel-partner-modal">Cancel</button>
                <button type="submit" data-testid="save-partner" className="px-6 py-2.5 bg-atf-brand text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-brand2">
                  {editing ? "Save changes" : "Add partner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const INPUT = "w-full border border-atf-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-atf-brand focus:ring-1 focus:ring-atf-brand";
const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 eyebrow text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left", mono }) => (
  <td className={`px-4 py-3 text-${align} ${mono ? "tabular" : ""}`}>{children}</td>
);
const Field = ({ label, children, required, full }) => (
  <div className={full ? "col-span-full" : ""}>
    <label className="eyebrow block mb-2">{label}{required && <span className="text-atf-negative ml-1">*</span>}</label>
    {children}
  </div>
);
